import {
  LanguageNameSpaces,
  MarketplacePaymentDetails,
  OpenMessage,
  PaymentDetails,
  SubscriptionPaymentMethodType,
} from "@shared/types";
import { useTheme } from "styled-components";
import { PaymentStatus } from "@shared/features/settings/subscription/types";
import { Color } from "@shared/common/Themes/types";
import { PAYMENT_OPTION_TO_DISPLAY_NAME } from "@shared/features/settings/subscription/constants";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";

interface PaymentStatusTagProps {
  paymentDetails: PaymentDetails | MarketplacePaymentDetails;
  openMessage: OpenMessage;
}

const usePaymentStatus = ({
  paymentDetails,
  openMessage,
}: PaymentStatusTagProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BILLING);
  const theme = useTheme();
  const lowerStatus = paymentDetails.status.toLowerCase();
  const status = paymentDetails.status;
  const link =
    "payer_view_url" in paymentDetails
      ? paymentDetails.payer_view_url
      : paymentDetails.payment_url;
  let linkErrorMessage: string | undefined;
  if (lowerStatus !== "pending") {
    linkErrorMessage = t("payment_status.invoice_pending_only");
  } else if (!link) {
    linkErrorMessage = t("payment_status.no_invoice_available");
  }
  const isAppleOrGooglePay =
    paymentDetails.payment_option ===
      PAYMENT_OPTION_TO_DISPLAY_NAME[
        SubscriptionPaymentMethodType.APPLEPAY_SUBSCRIPTION
      ] ||
    paymentDetails.payment_option ===
      PAYMENT_OPTION_TO_DISPLAY_NAME[
        SubscriptionPaymentMethodType.GOOGLEPAY_SUBSCRIPTION
      ];
  const isClickable = !linkErrorMessage && !isAppleOrGooglePay;

  let color: Color = "primaryBlue";
  if (lowerStatus === PaymentStatus.PENDING) {
    color = "gold";
  } else if (lowerStatus === PaymentStatus.CANCELLED) {
    color = "red";
  } else if (lowerStatus === PaymentStatus.PAID) {
    color = "green";
  } else if (lowerStatus === PaymentStatus.EXPIRED) {
    color = "red";
  }

  const onClick = () => {
    if (isAppleOrGooglePay) return;
    if (linkErrorMessage) {
      openMessage(linkErrorMessage, "warning");
    }
  };

  return {
    theme,
    lowerStatus,
    status,
    link,
    linkErrorMessage,
    color,
    onClick,
    isClickable,
  };
};

export default usePaymentStatus;
